import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, Users, UserCheck, Home, Search, GraduationCap } from 'lucide-react';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from './ui/command';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';

const NAV_BY_ROLE = {
  bendahara: [
    { to: '/bendahara', key: 'dashboard', icon: LayoutDashboard },
    { to: '/bendahara/pelajar', key: 'pelajarKelas', icon: Users },
    { to: '/bendahara/pengguna', key: 'pengguna', icon: UserCheck },
  ],
  guru: [{ to: '/guru', key: 'dashboardKelas', icon: LayoutDashboard }],
  orangtua: [{ to: '/orangtua', key: 'anakSaya', icon: Home }],
};

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [students, setStudents] = useState([]);
  const { role } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const down = (e) => {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const openEvt = () => setOpen(true);
    document.addEventListener('keydown', down);
    window.addEventListener('open-command', openEvt);
    return () => {
      document.removeEventListener('keydown', down);
      window.removeEventListener('open-command', openEvt);
    };
  }, []);

  const searchStudents = useCallback(async (q) => {
    if (!q || q.length < 2 || role === 'orangtua') { setStudents([]); return; }
    const { data } = await supabase
      .from('students')
      .select('id, nama, classes(nama)')
      .ilike('nama', `%${q}%`)
      .limit(6);
    setStudents(data || []);
  }, [role]);

  useEffect(() => {
    const id = setTimeout(() => searchStudents(query), 200);
    return () => clearTimeout(id);
  }, [query, searchStudents]);

  const go = (to) => { setOpen(false); navigate(to); };

  const navItems = NAV_BY_ROLE[role] || [];

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder={t('cariPelajar')}
        value={query}
        onValueChange={setQuery}
        data-testid="command-input"
      />
      <CommandList>
        <CommandEmpty>{t('tiadaData')}</CommandEmpty>
        {students.length > 0 && (
          <CommandGroup heading={t('pelajarKelas')}>
            {students.map((s) => (
              <CommandItem
                key={s.id}
                value={`stu-${s.id}-${s.nama}`}
                onSelect={() => go(role === 'guru' ? '/guru' : `/bendahara/pelajar/${s.id}`)}
                data-testid={`command-student-${s.id}`}
              >
                <GraduationCap className="mr-2 h-4 w-4 text-emerald2" />
                <span>{s.nama}</span>
                <span className="ml-auto text-xs text-muted-foreground">{s.classes?.nama}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        <CommandGroup heading="Navigasi">
          {navItems.map((n) => (
            <CommandItem key={n.to} value={t(n.key)} onSelect={() => go(n.to)} data-testid={`command-nav-${n.key}`}>
              <n.icon className="mr-2 h-4 w-4 text-brand" />
              {t(n.key)}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

export function CommandTrigger() {
  const { t } = useTranslation();
  return (
    <button
      onClick={() => window.dispatchEvent(new Event('open-command'))}
      data-testid="command-trigger"
      className="inline-flex items-center gap-2 h-11 px-3 rounded-lg border border-border bg-card text-muted-foreground hover:bg-muted transition-colors text-sm min-w-[180px] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
    >
      <Search className="h-4 w-4" />
      <span className="flex-1 text-left">{t('cariPelajar')}</span>
      <kbd className="hidden sm:inline text-[10px] font-mono bg-muted border border-border rounded px-1.5 py-0.5">⌘K</kbd>
    </button>
  );
}
